import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, ResponsiveContainer } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H2, Text } from 'app/common/typography'
import { Link } from 'app/common/link'
import { ErrorManagement } from 'app/common/error-management'
import { Layout } from 'app/common/layout'
import { CircleChecked, CircleClose } from 'app/assets/icons'
import type { EventMetadata } from 'shared/store/metadata'
import { Recaptcha } from 'app/common/recaptcha'
import studentBanner from 'shared/assets/images/student-blazon/studentBanner.png'
import studentBannerMobile from 'shared/assets/images/student-blazon/studentBannerMobile.png'
import { SPACING } from 'app-v2/design-tokens'
import { isMobile } from 'react-device-detect'

type Props = {
    getUrlParams: () => void,
    resendMail: (queen: string) => void,
    success: boolean,
    intl: IntlShape,
    activateError: any,
    error: any,
    networkError: any,
    sendMeta: EventMetadata => void,
    authenticated: boolean
}

type State = {
    showCaptcha: boolean
}

class Congratulations extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    componentDidMount() {
        const { sendMeta } = this.props
        this.props.getUrlParams()
        const meta = {
            event: 'pageview',
            page: {
                category: 'felicitations'
            }
        }
        // $FlowFixMe EventMetadata type ...
        sendMeta(meta)
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    render() {
        const {
            resendMail,
            success,
            activateError,
            error,
            networkError,
            intl
        } = this.props
        const { showCaptcha } = this.state
        const { yellow } = colors

        const queryString = window.location.search
        const params = new URLSearchParams(queryString)

        const isStudent = params.get('student') === 'true'

        return (
            <ErrorManagement error={error} networkError={networkError}>
                {showCaptcha && (
                    <Recaptcha
                        action="WebMailCongratulation"
                        verifyCallback={this.verifyCallback}
                    />
                )}

                <Layout background="plain" border>
                    <StyledResponsiveContainer>
                        {success && (
                            <Fragment>
                                <FormattedMessage id="congratulations.success.title">
                                    {(text: string) => (
                                        <StyledH2>{text}</StyledH2>
                                    )}
                                </FormattedMessage>
                                <FormattedMessage id="congratulations.success.baseline">
                                    {(text: string) => (
                                        <StyledH2>{text}</StyledH2>
                                    )}
                                </FormattedMessage>
                                <Paragraph>
                                    <CircleChecked />
                                </Paragraph>
                                <FormattedMessage id="congratulations.success.message">
                                    {(text: string) => (
                                        <Paragraph>
                                            <Text>{text}</Text>
                                        </Paragraph>
                                    )}
                                </FormattedMessage>
                                <Link
                                    url={intl.formatMessage({
                                        id: this.props.authenticated
                                            ? 'routes.kingdom.dashboard'
                                            : 'routes.signin'
                                    })}>
                                    <FormattedMessage
                                        id={
                                            this.props.authenticated
                                                ? 'congratulations.success-authed.button.label'
                                                : 'congratulations.success.button.label'
                                        }>
                                        {(text: string) => (
                                            <StyleButtonMediumSuccess
                                                background={yellow}
                                                border="none">
                                                {text}
                                            </StyleButtonMediumSuccess>
                                        )}
                                    </FormattedMessage>
                                </Link>
                            </Fragment>
                        )}
                        {activateError && (
                            <Fragment>
                                <Paragraph>
                                    <CircleClose color="white" />
                                </Paragraph>
                                <Paragraph>
                                    <FormattedMessage id="congratulations.error.message">
                                        {(text: string) => <Text>{text}</Text>}
                                    </FormattedMessage>
                                </Paragraph>
                                <Paragraph />
                                <FormattedMessage id="congratulations.error.button.label">
                                    {(text: string) => (
                                        <StyleButtonMediumSuccess
                                            onClick={async () => {
                                                try {
                                                    this.setState({
                                                        showCaptcha: true
                                                    })
                                                    const recaptchaToken = await this
                                                        .recaptchaPromise
                                                    resendMail(recaptchaToken)
                                                } finally {
                                                    this.setState({
                                                        showCaptcha: false
                                                    })
                                                    this.initRecaptchaPromise()
                                                }
                                            }}
                                            background={yellow}
                                            border="none">
                                            {text}
                                        </StyleButtonMediumSuccess>
                                    )}
                                </FormattedMessage>
                            </Fragment>
                        )}
                    </StyledResponsiveContainer>

                    {isStudent && (
                        <>
                            {isMobile ? (
                                <StyledStudentBannerMobile
                                    src={studentBannerMobile}
                                />
                            ) : (
                                <StyledStudentBanner src={studentBanner} />
                            )}
                        </>
                    )}
                </Layout>
            </ErrorManagement>
        )
    }
}

const StyledStudentBannerMobile = styled.img`
    max-width: calc(100% - ${SPACING.XXL});
    padding: ${SPACING.S};
`

const StyledStudentBanner = styled.img`
    width: 100%;
`
const StyledResponsiveContainer = styled(ResponsiveContainer)`
    margin-top: 150px;
`

const Paragraph = styled.p`
    text-align: center;
`

const StyledH2 = styled(H2)`
    text-align: center;
`

const StyleButtonMediumSuccess = styled(ButtonMedium)`
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    margin: 30px 0;
`

const CongratulationsComponent = injectIntl(Congratulations)

export { CongratulationsComponent }
